/**
 * Geometrie des Kachel-Atlas.
 *
 * Die geänderten Kacheln werden dicht in ein möglichst quadratisches Raster
 * gepackt, statt sie – wie im Original – an ihrer Originalposition auf ein
 * bildschirmgroßes, weiß gefülltes Bild zu zeichnen. Bei 20 geänderten
 * Kacheln entsteht so ein Atlas von 200x160 statt eines Bildes von 1920x1080.
 *
 * Jede Zelle ist ATLAS_CELL groß: TILE_DIM Nutzfläche plus ATLAS_PADDING
 * ringsum. Der Rand wird aus dem Quellbild mitkopiert und beim Empfänger
 * verworfen; er verhindert, dass Kompressionsartefakte über die Kachelgrenze
 * bluten (im Original gelöst über 42x42-Ausschnitte mit 5px Rückversatz).
 */

import { ATLAS_CELL, ATLAS_PADDING, TILE_DIM } from './constants.ts';

/** Spaltenzahl des Atlas für n Kacheln. */
export function atlasCols(count: number): number {
  return Math.max(1, Math.ceil(Math.sqrt(count)));
}

/** Pixelmaße des Atlas für n Kacheln. */
export function atlasSize(count: number): { width: number; height: number } {
  const cols = atlasCols(count);
  const rows = Math.max(1, Math.ceil(count / cols));
  return { width: cols * ATLAS_CELL, height: rows * ATLAS_CELL };
}

/** Linke obere Ecke der Zelle mit dem gegebenen Index (inklusive Rand). */
export function cellOrigin(cellIndex: number, cols: number): { x: number; y: number } {
  return {
    x: (cellIndex % cols) * ATLAS_CELL,
    y: Math.floor(cellIndex / cols) * ATLAS_CELL,
  };
}

/** Linke obere Ecke der Nutzfläche innerhalb der Zelle (ohne Rand). */
export function cellContentOrigin(cellIndex: number, cols: number): { x: number; y: number } {
  const { x, y } = cellOrigin(cellIndex, cols);
  return { x: x + ATLAS_PADDING, y: y + ATLAS_PADDING };
}

/** Position einer Kachel im Vollbild, aus ihrem row-major Index. */
export function tilePosition(tileIndex: number, tilesX: number): { x: number; y: number } {
  return {
    x: (tileIndex % tilesX) * TILE_DIM,
    y: Math.floor(tileIndex / tilesX) * TILE_DIM,
  };
}

/**
 * Rechteck, das für eine Kachel aus dem Quellbild in den Atlas kopiert wird –
 * inklusive Rand, an den Bildkanten geklemmt. `destX`/`destY` sind bereits so
 * verschoben, dass die Nutzfläche in der Zelle immer bei +ATLAS_PADDING liegt.
 */
export interface CopyRect {
  srcX: number;
  srcY: number;
  width: number;
  height: number;
  destX: number;
  destY: number;
}

export function tileCopyRect(
  tileIndex: number,
  cellIndex: number,
  tilesX: number,
  cols: number,
  imageWidth: number,
  imageHeight: number,
): CopyRect {
  const tile = tilePosition(tileIndex, tilesX);
  const cell = cellOrigin(cellIndex, cols);

  // Gewünschter Ausschnitt inklusive Rand …
  const wantX = tile.x - ATLAS_PADDING;
  const wantY = tile.y - ATLAS_PADDING;

  // … auf die Bildgrenzen geklemmt.
  const srcX = Math.max(0, wantX);
  const srcY = Math.max(0, wantY);
  const srcRight = Math.min(imageWidth, wantX + ATLAS_CELL);
  const srcBottom = Math.min(imageHeight, wantY + ATLAS_CELL);

  return {
    srcX,
    srcY,
    width: Math.max(0, srcRight - srcX),
    height: Math.max(0, srcBottom - srcY),
    // Verschiebung um denselben Betrag, um den geklemmt wurde.
    destX: cell.x + (srcX - wantX),
    destY: cell.y + (srcY - wantY),
  };
}
